import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { buildCompressibleMimeTypes } from '~/utils/fileUpload';

export function useFileAPI() {
  const { errors, request } = useRequest();
  const platformStore = usePlatformStore();
  const settingsStore = useSettingsStore();

  const compressibleMimeTypes = computed(() => buildCompressibleMimeTypes(settingsStore._settings.convertHeic));

  const { t } = useEkI18n();
  const { openToast } = useUserMessages();
  const appApiFetch = getAppApiFetch();
  const appApiUpload = getAppApiUpload();

  async function upload(url: string, formData: FormData, onProgress?: (ratio: number, total: number) => void) {
    try {
      const response = onProgress
        ? await appApiUpload<ResponseDTO<boolean>>(url, formData, onProgress)
        : await appApiFetch<ResponseDTO<boolean>>(url, { method: 'POST', body: formData });

      return response?.HasErrors === false ? response.Data : false;
    } catch {
      return false;
    }
  }

  function buildCompressibleFormData(fileData: Blob, mimeType: string, fileName: string, address: GeolocationDto | undefined, comment: string | undefined): FormData {
    const formData = new FormData();
    const safeMime = mimeType || 'application/octet-stream';
    formData.append('file', new File([fileData], fileName, { type: safeMime }));
    formData.append('MetadataDTO.StreetName', address?.StreetName ?? '');
    formData.append('MetadataDTO.StreetNumber', address?.StreetNumber ?? '');
    formData.append('MetadataDTO.Unit', address?.Unit ?? '');
    formData.append('MetadataDTO.PostalCode', address?.PostalCode ?? '');
    formData.append('MetadataDTO.City', address?.City ?? '');
    formData.append('MetadataDTO.Country', address?.Country ?? '');
    formData.append('MetadataDTO.CountryISO', address?.CountryCode ?? '');
    formData.append('MetadataDTO.Comment', comment ?? '');

    return formData;
  }

  function normalizeZipMimeType(mimeType: string): string {
    const zipVariants = ['application/x-zip-compressed', 'application/x-zip', 'application/zip-compressed', 'multipart/x-zip'];

    return zipVariants.includes(mimeType) ? 'application/zip' : mimeType;
  }

  function buildNonCompressibleFormData(fileData: Blob, mimeType: string, fileName: string, address: GeolocationDto | undefined, comment: string | undefined): FormData {
    const formData = new FormData();
    const metadata = JSON.stringify({
      StreetName: address?.StreetName ?? '',
      StreetNumber: address?.StreetNumber ?? '',
      Unit: address?.Unit ?? '',
      PostalCode: address?.PostalCode ?? '',
      City: address?.City ?? '',
      Country: address?.Country ?? '',
      CountryISO: address?.CountryCode ?? '',
      Comment: comment ?? ''
    });
    formData.append('metadata', new Blob([metadata], { type: 'application/json' }));
    const normalizedMimeType = normalizeZipMimeType(mimeType) || 'application/octet-stream';
    formData.append('file', new File([fileData], fileName, { type: normalizedMimeType }));

    return formData;
  }

  async function uploadToQA(fileData: Blob, mimeType: string, fileName: string, address: GeolocationDto | undefined, comment: string | undefined, qaAnswerBody: number, onProgress?: (ratio: number, total: number) => void) {
    if (compressibleMimeTypes.value.has(mimeType)) {
      return upload(`${versionedUrl('File/StreamUploadCompressible')}?category=QA&entityId=${qaAnswerBody}`, buildCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
    }

    return upload(`${versionedUrl('File/StreamUploadNonCompressible')}?category=QA&entityId=${qaAnswerBody}`, buildNonCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
  }

  async function uploadToProject(fileData: Blob, mimeType: string, fileName: string, address: GeolocationDto | undefined, comment: string | undefined, projectId: number, folderId: number, onProgress?: (ratio: number, total: number) => void) {
    if (compressibleMimeTypes.value.has(mimeType)) {
      return upload(`${versionedUrl('File/StreamUploadCompressible')}?category=Project&entityId=${projectId}&folderId=${folderId}`, buildCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
    }

    return upload(`${versionedUrl('File/StreamUploadNonCompressible')}?category=Project&entityId=${projectId}&folderId=${folderId}`, buildNonCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
  }

  async function uploadToExpense(fileData: Blob, mimeType: string, fileName: string, address: GeolocationDto | undefined, comment: string | undefined, expenseId: number, onProgress?: (ratio: number, total: number) => void) {
    if (compressibleMimeTypes.value.has(mimeType)) {
      return upload(`${versionedUrl('File/StreamUploadCompressible')}?category=Expense&entityId=${expenseId}`, buildCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
    }

    return upload(`${versionedUrl('File/StreamUploadNonCompressible')}?category=Expense&entityId=${expenseId}`, buildNonCompressibleFormData(fileData, mimeType, fileName, address, comment), onProgress);
  }

  interface FileToOpen {
    name: string;
    mimeType: string;
    data: string;
  }

  async function open(file: FileToOpen) {
    if (!file.data.length) {
      openToast(t('documents.userMessages.cannotOpenFile'), ToastType.ERROR);

      return;
    }

    try {
      if (platformStore.isWeb) {
        await FileOpener.openFile({
          blob: base64ToBlob(file.data, file.mimeType),
          mimeType: file.mimeType
        });
      } else if (platformStore.isMobile) {
        const result = await Filesystem.writeFile({
          path: file.name,
          data: file.data,
          directory: Directory.Documents
        });
        await FileOpener.openFile({
          path: result.uri,
          mimeType: file.mimeType
        });
      }
    } catch {
      openToast(t('documents.userMessages.cannotOpenFile'), ToastType.ERROR);
    }
  }

  async function getGeolocations(coordinates: Coordinate[]) {
    const url = versionedUrl('File/GetGeolocations');

    return await request<GeolocationDto[]>(url, {
      method: 'POST',
      body: coordinates
    });
  }

  async function updateFileMetadata(fileInfoId: number, metadata: {
    FileName?: string,
    StreetName?: string,
    StreetNumber?: string,
    Unit?: string,
    PostalCode?: string,
    City?: string,
    Country?: string,
    CountryISO?: string,
    Comment?: string
  }) {
    return await request<boolean>(versionedUrl('File/UpsertFileMetadata'), {
      method: 'POST',
      body: { FileInfoID: fileInfoId, ...metadata }
    });
  }

  return {
    errors,
    uploadToProject,
    uploadToExpense,
    uploadToQA,
    open,
    getGeolocations,
    updateFileMetadata
  };
}
